/**
 * Registrations Management Page
 *
 * Integrated workflow for participant registration approvals,
 * program registration form configuration (open/close & auto/manual approval),
 * and WhatsApp/Telegram community invitation link management.
 */

import { useState } from 'react';
import {
  Check,
  X,
  MessageCircle,
  MessageSquare,
  Loader2,
  ChevronRight,
  RotateCcw,
  Sparkles,
  Globe,
  Copy,
  ExternalLink,
  Pencil,
  Plus,
  Users,
  Sliders,
  ClipboardList,
} from 'lucide-react';
import { StatusChip, FilterBar, Button, type FilterOption } from '@lms/ui';
import {
  useRegistrations,
  useApproveRegistration,
  useRejectRegistration,
  isIncompleteUserProfileError,
} from '../hooks/useRegistrations';
import { usePrograms, useProgramsOverview, useUpdateProgram } from '../hooks/usePrograms';
import { useWhatsAppGroups } from '../hooks/useWhatsAppGroups';
import { useAuthStore } from '../store/auth';
import type { RegistrationStatus, Program, WhatsAppGroup } from '@lms/shared/types';
import { Link } from 'react-router';
import { RejectionModal } from '../components/RejectionModal';
import { EditUserModal, type MissingProfileField } from '../components/EditUserModal';
import { ProgramModal } from '../components/ProgramModal';
import { WhatsAppGroupModal } from '../components/WhatsAppGroupModal';
import { copy } from '../copy/copy';
import { Bilingual } from '../components/Bilingual';
import { useLanguage } from '../lib/LanguageContext';
import { useNotificationStore } from '../store/notifications';

type StatusFilter = 'ALL' | RegistrationStatus;
type RegistrationTab = 'LIST' | 'SETTINGS' | 'WHATSAPP';

export function Registrations() {
  const { user } = useAuthStore();
  const { language, t } = useLanguage();
  const { addNotification } = useNotificationStore();

  const [activeMainTab, setActiveMainTab] = useState<RegistrationTab>('LIST');
  const [filterStatus, setFilterStatus] = useState<StatusFilter>('ALL');
  const [resubmittedOnly, setResubmittedOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const limit = 10;
  const [rejectionModalId, setRejectionModalId] = useState<string | null>(null);

  // Group and Program modal state
  const [editingProgram, setEditingProgram] = useState<Program | null>(null);
  const [isWhatsAppModalOpen, setIsWhatsAppModalOpen] = useState(false);
  const [editingWhatsAppGroup, setEditingWhatsAppGroup] = useState<WhatsAppGroup | undefined>();
  const [selectedWhatsAppProgramId, setSelectedWhatsAppProgramId] = useState<string>('ALL');
  const [selectedWhatsAppGender, setSelectedWhatsAppGender] = useState<string>('ALL');
  const [copiedProgramCode, setCopiedProgramCode] = useState<string | null>(null);
  const [copiedGroupLink, setCopiedGroupLink] = useState<string | null>(null);

  const [editUserContext, setEditUserContext] = useState<{
    userId: string;
    registrationId: string;
    missingFields: MissingProfileField[];
    reason: string;
  } | null>(null);

  const { data, isLoading } = useRegistrations({
    page,
    limit,
    status: filterStatus === 'ALL' ? undefined : (filterStatus as RegistrationStatus),
    search: searchQuery || undefined,
    resubmitted: resubmittedOnly || undefined,
  });

  const { data: programsData, isLoading: isProgramsLoading } = usePrograms({ limit: 100 });
  const { data: groupsData, isLoading: isGroupsLoading } = useWhatsAppGroups();
  const updateProgramMutation = useUpdateProgram();

  const approveMutation = useApproveRegistration();
  const rejectMutation = useRejectRegistration();

  const registrations = data?.items || [];
  const programsList = Array.isArray(programsData?.items) ? (programsData.items as Program[]) : [];
  const groupsList = Array.isArray(groupsData) ? (groupsData as WhatsAppGroup[]) : [];

  const handleApprove = (id: string) => {
    if (!user) return;
    approveMutation.mutate(
      {
        id,
        adminNotes: 'Approved via Admin Panel',
      },
      {
        onError: err => {
          // Surface the 409 "Incomplete User Profile" inline by
          // auto-opening the EditUserModal prefilled with the missing
          // fields. The user updates the profile, we retry approve.
          if (isIncompleteUserProfileError(err)) {
            setEditUserContext({
              userId: err.body.userId,
              registrationId: err.body.registrationId,
              missingFields: err.body.missingFields,
              reason: err.body?.message || err.message,
            });
          }
        },
      }
    );
  };

  const handleReject = (id: string, reason: string) => {
    rejectMutation.mutate(
      {
        id,
        adminNotes: reason,
      },
      {
        onSuccess: () => {
          setRejectionModalId(null);
        },
      }
    );
  };

  const getStatusVariant = (
    status: RegistrationStatus
  ): 'success' | 'warning' | 'error' | 'neutral' => {
    switch (status) {
      case 'PENDING':
        return 'warning';
      case 'APPROVED':
        return 'success';
      case 'REJECTED':
        return 'error';
      case 'REVISION':
        return 'warning';
      default:
        return 'neutral';
    }
  };

  const filterOptions: FilterOption[] = [
    {
      value: 'ALL',
      label: t(copy.registrations.filterAll),
      count: filterStatus === 'ALL' ? registrations.length : undefined,
    },
    {
      value: 'PENDING',
      label: t(copy.registrations.filterPending),
    },
    {
      value: 'APPROVED',
      label: t(copy.registrations.filterApproved),
    },
    {
      value: 'REJECTED',
      label: t(copy.registrations.filterRejected),
    },
    {
      value: 'REVISION',
      label: t(copy.registrations.filterRevision),
    },
  ];

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (date: Date | string | undefined) => {
    if (!date) return '-';
    return new Date(date).toLocaleString(language === 'ar' ? 'ar-EG' : 'id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const { data: overview } = useProgramsOverview();

  const filteredGroups = groupsList.filter(group => {
    if (selectedWhatsAppProgramId !== 'ALL' && group.programId !== selectedWhatsAppProgramId) {
      return false;
    }
    if (selectedWhatsAppGender !== 'ALL' && group.gender !== selectedWhatsAppGender) {
      return false;
    }
    return true;
  });

  if (isLoading && activeMainTab === 'LIST') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
        <p className="text-sm font-body text-on-surface-variant">
          <Bilingual {...copy.common.loading} />
        </p>
      </div>
    );
  }

  const stats = overview?.stats;

  return (
    <div className="space-y-12 animate-fadeIn">
      {/* Editorial Header Section */}
      <section className="flex flex-col md:flex-row justify-between items-start md:items-end gap-10 pb-6 border-b border-outline-variant/10">
        <div className="space-y-1">
          <h1 className="text-4xl font-headline font-black text-on-surface tracking-tight leading-none m-0">
            <Bilingual {...copy.registrations.listTitle} />
          </h1>
          <p className="text-lg font-body text-on-surface-variant max-w-2xl">
            <Bilingual {...copy.registrations.listSubtitle} />
          </p>
        </div>

        {/* Metric Overview (Architect Style) */}
        <div className="flex gap-12">
          <div>
            <p className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-[0.2em] mb-2">
              <Bilingual {...copy.common.total} />
            </p>
            <p className="text-2xl font-headline font-black text-on-surface m-0">
              {stats?.totalRegistrations ?? data?.meta.total ?? '...'}
            </p>
          </div>
          <div>
            <p className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-[0.2em] mb-2">
              <Bilingual {...copy.registrations.filterPending} />
            </p>
            <div className="flex items-center gap-3">
              <p className="text-2xl font-headline font-black text-secondary m-0">
                {stats?.pendingRegistrations ?? '...'}
              </p>
              {stats?.pendingToday ? (
                <span className="text-[10px] font-mono font-bold text-secondary opacity-60">
                  +{stats.pendingToday} {t(copy.registrations.today)}
                </span>
              ) : null}
            </div>
          </div>
          <div>
            <p className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-[0.2em] mb-2">
              <Bilingual {...copy.registrations.filterApproved} />
            </p>
            <p className="text-2xl font-headline font-black text-primary m-0">
              {stats?.approvedRegistrations ?? '...'}
            </p>
          </div>
        </div>
      </section>

      {/* Main Feature Tabs */}
      <section className="flex flex-wrap items-center gap-3 border-b border-outline-variant/10 pb-4">
        <button
          type="button"
          onClick={() => setActiveMainTab('LIST')}
          className={`flex items-center gap-2.5 px-5 py-3 rounded-2xl text-xs font-label font-bold uppercase tracking-wider transition-all ${
            activeMainTab === 'LIST'
              ? 'bg-primary text-on-primary shadow-level2'
              : 'bg-surface-container text-on-surface-variant hover:bg-surface-container-high'
          }`}
        >
          <ClipboardList size={16} />
          <Bilingual {...copy.registrations.tabList} />
          {data?.meta.total !== undefined && (
            <span
              className={`px-2 py-0.5 rounded-full text-[10px] font-mono ${
                activeMainTab === 'LIST'
                  ? 'bg-white/20 text-white'
                  : 'bg-surface-container-highest text-on-surface-variant'
              }`}
            >
              {data.meta.total}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={() => setActiveMainTab('SETTINGS')}
          className={`flex items-center gap-2.5 px-5 py-3 rounded-2xl text-xs font-label font-bold uppercase tracking-wider transition-all ${
            activeMainTab === 'SETTINGS'
              ? 'bg-primary text-on-primary shadow-level2'
              : 'bg-surface-container text-on-surface-variant hover:bg-surface-container-high'
          }`}
        >
          <Sliders size={16} />
          <Bilingual {...copy.registrations.tabProgramSettings} />
          {programsList.length > 0 && (
            <span
              className={`px-2 py-0.5 rounded-full text-[10px] font-mono ${
                activeMainTab === 'SETTINGS'
                  ? 'bg-white/20 text-white'
                  : 'bg-surface-container-highest text-on-surface-variant'
              }`}
            >
              {programsList.length}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={() => setActiveMainTab('WHATSAPP')}
          className={`flex items-center gap-2.5 px-5 py-3 rounded-2xl text-xs font-label font-bold uppercase tracking-wider transition-all ${
            activeMainTab === 'WHATSAPP'
              ? 'bg-primary text-on-primary shadow-level2'
              : 'bg-surface-container text-on-surface-variant hover:bg-surface-container-high'
          }`}
        >
          <MessageSquare size={16} />
          <Bilingual {...copy.registrations.tabCommunityGroups} />
          {groupsList.length > 0 && (
            <span
              className={`px-2 py-0.5 rounded-full text-[10px] font-mono ${
                activeMainTab === 'WHATSAPP'
                  ? 'bg-white/20 text-white'
                  : 'bg-surface-container-highest text-on-surface-variant'
              }`}
            >
              {groupsList.length}
            </span>
          )}
        </button>
      </section>

      {/* TAB 1: REGISTRATIONS LIST & APPROVALS */}
      {activeMainTab === 'LIST' && (
        <div className="space-y-8 animate-fadeIn">
          {/* Control Surface */}
          <section className="flex flex-wrap items-center justify-between gap-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className="relative group">
                <input
                  aria-label="Filter registrations"
                  className="bg-surface-container-high border-none rounded-lg pl-10 pr-4 py-2 text-sm w-72 focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                  placeholder={t(copy.registrations.searchPlaceholder)}
                  type="text"
                  value={searchQuery}
                  onChange={e => {
                    setSearchQuery(e.target.value);
                    setPage(1);
                  }}
                />
              </div>

              <FilterBar
                options={filterOptions}
                value={filterStatus}
                onChange={value => {
                  setFilterStatus(value as StatusFilter);
                  setPage(1);
                }}
                className="!m-0"
              />

              <button
                type="button"
                onClick={() => {
                  setResubmittedOnly(prev => !prev);
                  setPage(1);
                }}
                aria-pressed={resubmittedOnly}
                title={
                  language === 'ar'
                    ? copy.registrations.filterResubmittedHint.ar
                    : copy.registrations.filterResubmittedHint.id
                }
                className={
                  'flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-label font-medium transition-colors ' +
                  (resubmittedOnly
                    ? 'bg-primary text-on-primary shadow-level1'
                    : 'bg-surface-container-high text-on-surface-variant hover:bg-surface-container')
                }
              >
                <RotateCcw size={14} />
                <Bilingual {...copy.registrations.filterResubmitted} />
              </button>
            </div>
          </section>

          {/* Operational Table */}
          <div className="bg-surface-container-lowest rounded-2xl overflow-hidden shadow-level1 border border-on-surface/5">
            {/* Table Header (Hidden on Mobile) */}
            <div className="hidden md:grid grid-cols-[1.5fr_1.5fr_1.5fr_1fr_1fr_1fr_180px] gap-6 px-8 py-5 bg-surface-container-low/50 border-b border-on-surface/5">
              {[
                copy.registrations.tableHeaderParticipant,
                copy.registrations.tableHeaderEmail,
                copy.registrations.tableHeaderProgram,
                copy.registrations.tableHeaderCode,
                copy.registrations.tableHeaderStatus,
                copy.registrations.tableHeaderTimestamp,
                copy.registrations.tableHeaderActions,
              ].map((h, idx) => (
                <div
                  key={idx}
                  className={`text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-[0.15em] opacity-60 ${
                    idx === 6 ? (language === 'ar' ? 'text-left' : 'text-right') : ''
                  }`}
                >
                  <Bilingual {...h} />
                </div>
              ))}
            </div>

            {/* Table Body */}
            <div className="divide-y divide-on-surface/5">
              {registrations.map(reg => (
                <div
                  key={reg.id}
                  className="flex flex-col md:grid md:grid-cols-[1.5fr_1.5fr_1.5fr_1fr_1fr_1fr_180px] gap-4 md:gap-6 p-6 md:px-8 md:py-5 hover:bg-surface-container-low/30 transition-colors duration-150 items-start md:items-center group"
                >
                  {/* Participant Identity */}
                  <div className="flex items-center gap-4 w-full md:w-auto">
                    <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-sm shrink-0 font-headline">
                      {reg.user?.name?.charAt(0) || 'U'}
                    </div>
                    <div className="min-w-0">
                      <div className="font-body font-bold text-on-surface text-sm tracking-tight truncate flex items-center gap-2">
                        <span>{reg.user?.name || 'Unknown'}</span>
                        {reg.lastResubmittedAt && (
                          <span
                            title={
                              language === 'ar'
                                ? `أُعيد تقديمه في ${formatDateTime(reg.lastResubmittedAt)}`
                                : `Diajukan ulang pada ${formatDateTime(reg.lastResubmittedAt)}`
                            }
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-label font-bold uppercase tracking-wider bg-secondary/15 text-secondary shrink-0"
                          >
                            <RotateCcw size={10} />
                            <Bilingual {...copy.registrations.resubmittedBadge} />
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-on-surface-variant font-mono opacity-60 mt-0.5 truncate">
                        {reg.user?.phone || '-'}
                      </div>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="text-xs font-body text-on-surface-variant truncate w-full md:w-auto">
                    <span className="md:hidden text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest block opacity-60 mb-1">
                      <Bilingual {...copy.registrations.tableHeaderEmail} />
                    </span>
                    {reg.user?.email || '-'}
                  </div>

                  {/* Program Context */}
                  <div className="w-full md:w-auto">
                    <span className="md:hidden text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest block opacity-60 mb-1">
                      <Bilingual {...copy.registrations.tableHeaderProgram} />
                    </span>
                    <span className="text-xs font-body font-bold text-on-surface block truncate">
                      {reg.program?.name || '-'}
                    </span>
                    <span className="text-[10px] text-on-surface-variant font-mono opacity-60">
                      {reg.program?.code || ''}
                    </span>
                  </div>

                  {/* Participant NIP Code */}
                  <div className="w-full md:w-auto">
                    <span className="md:hidden text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest block opacity-60 mb-1">
                      <Bilingual {...copy.registrations.tableHeaderCode} />
                    </span>
                    <span className="text-xs font-mono font-bold text-primary bg-primary/5 px-2 py-1 rounded">
                      {reg.participantId?.participantCode || '-'}
                    </span>
                  </div>

                  {/* Status Indicator */}
                  <div className="w-full md:w-auto">
                    <span className="md:hidden text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest block opacity-60 mb-1">
                      <Bilingual {...copy.registrations.tableHeaderStatus} />
                    </span>
                    <StatusChip variant={getStatusVariant(reg.status)} label={reg.status} />
                  </div>

                  {/* Registration Date */}
                  <div className="w-full md:w-auto">
                    <span className="md:hidden text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest block opacity-60 mb-1">
                      <Bilingual {...copy.registrations.tableHeaderTimestamp} />
                    </span>
                    <span className="text-xs font-body text-on-surface-variant">
                      {formatDate(reg.registeredAt)}
                    </span>
                  </div>

                  {/* Action Controls */}
                  <div
                    className={`flex items-center gap-2 w-full md:w-auto justify-end pt-4 md:pt-0 border-t md:border-t-0 border-on-surface/5 ${
                      language === 'ar' ? 'md:justify-start' : 'md:justify-end'
                    }`}
                  >
                    {reg.status === 'PENDING' ? (
                      <>
                        <button
                          onClick={() => handleApprove(reg.id)}
                          disabled={approveMutation.isPending}
                          title={t(copy.registrations.btnApprove)}
                          className="w-8 h-8 rounded-lg bg-primary/10 hover:bg-primary text-primary hover:text-on-primary flex items-center justify-center transition-all disabled:opacity-50"
                        >
                          <Check size={14} />
                        </button>
                        <button
                          onClick={() => setRejectionModalId(reg.id)}
                          disabled={rejectMutation.isPending}
                          title={t(copy.registrations.btnReject)}
                          className="w-8 h-8 rounded-lg bg-error-container/30 hover:bg-error-container text-on-error-container flex items-center justify-center transition-all disabled:opacity-50"
                        >
                          <X size={14} />
                        </button>
                      </>
                    ) : null}
                    <Link
                      to={`/registrations/${reg.id}`}
                      className="w-8 h-8 rounded-lg bg-surface-container-high hover:bg-surface-container text-on-surface-variant flex items-center justify-center transition-all group-hover:text-primary"
                    >
                      <ChevronRight size={14} className={language === 'ar' ? 'rotate-180' : ''} />
                    </Link>
                  </div>
                </div>
              ))}

              {registrations.length === 0 && (
                <div className="p-16 text-center text-on-surface-variant opacity-60 font-body">
                  <Bilingual {...copy.registrations.emptyState} />
                </div>
              )}
            </div>

            {/* Pagination Controls */}
            {data?.meta && data.meta.totalPages > 1 && (
              <div className="px-8 py-4 bg-surface-container-low/30 border-t border-on-surface/5 flex items-center justify-between">
                <p className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                  {t(copy.registrations.pageIndicator)
                    .replace('{page}', String(page))
                    .replace('{totalPages}', String(data.meta.totalPages))}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage(prev => prev - 1)}
                    className="text-[10px] uppercase font-bold tracking-widest px-4"
                  >
                    <Bilingual {...copy.registrations.btnPrevious} />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= data.meta.totalPages}
                    onClick={() => setPage(prev => prev + 1)}
                    className="text-[10px] uppercase font-bold tracking-widest px-4"
                  >
                    <Bilingual {...copy.registrations.btnNext} />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 2: PROGRAM REGISTRATION FORM SETTINGS */}
      {activeMainTab === 'SETTINGS' && (
        <div className="space-y-8 animate-fadeIn">
          <div className="bg-surface-container-lowest p-6 sm:p-8 rounded-3xl shadow-level1 border border-on-surface/5 space-y-2">
            <div className="flex items-center gap-2 text-primary font-bold text-[10px] uppercase tracking-widest font-label">
              <Sparkles size={14} className="text-secondary" />
              <Bilingual {...copy.registrations.programSettingsTitle} />
            </div>
            <h2 className="text-2xl font-headline font-black text-on-surface m-0 tracking-tight">
              Kelola Form & Status Pendaftaran Tiap Program
            </h2>
            <p className="text-sm font-body text-on-surface-variant max-w-3xl opacity-70">
              <Bilingual {...copy.registrations.programSettingsDesc} />
            </p>
          </div>

          {isProgramsLoading ? (
            <div className="flex flex-col items-center justify-center min-h-[300px] space-y-4">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
              <p className="text-sm font-body text-on-surface-variant">Memuat program...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {programsList.map(prog => {
                const publicUrl = `https://my.tarbiyahsunnahlearning.or.id/daftar/${encodeURIComponent(prog.code)}`;
                const isCopied = copiedProgramCode === prog.code;

                return (
                  <div
                    key={prog.id}
                    className="bg-surface-container-lowest p-6 rounded-3xl shadow-level1 border border-on-surface/5 flex flex-col justify-between space-y-6 hover:shadow-level2 transition-all"
                  >
                    <div className="space-y-4">
                      {/* Program Header */}
                      <div className="flex justify-between items-start gap-4">
                        <div className="space-y-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-[10px] font-bold px-2 py-0.5 rounded-md bg-primary/10 text-primary">
                              {prog.code}
                            </span>
                            <span
                              className={`text-[10px] font-label font-bold uppercase tracking-wider px-2 py-0.5 rounded ${
                                prog.isRegistrationOpen !== false
                                  ? 'bg-emerald-100 text-emerald-800'
                                  : 'bg-surface-container-high text-on-surface-variant'
                              }`}
                            >
                              {prog.isRegistrationOpen !== false
                                ? 'Pendaftaran Dibuka'
                                : 'Pendaftaran Ditutup'}
                            </span>
                          </div>
                          <h3 className="text-lg font-headline font-bold text-on-surface m-0 truncate">
                            {prog.name}
                          </h3>
                        </div>

                        <Button
                          variant="ghost"
                          onClick={() => setEditingProgram(prog)}
                          className="p-2 rounded-xl text-on-surface-variant hover:text-primary hover:bg-surface-container border border-outline-variant/20 shrink-0"
                          title="Edit Konfigurasi Program"
                        >
                          <Pencil size={15} />
                        </Button>
                      </div>

                      {/* Controls Grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                        {/* Switch Status Buka/Tutup */}
                        <div className="p-3.5 rounded-2xl bg-surface-container-low/50 border border-on-surface/5 space-y-2">
                          <span className="text-[10px] font-label font-bold uppercase tracking-widest text-on-surface-variant opacity-60 block">
                            Status Pendaftaran
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              const next = prog.isRegistrationOpen === false;
                              updateProgramMutation.mutate({
                                id: prog.id,
                                data: { isRegistrationOpen: next },
                              });
                              addNotification({
                                type: 'success',
                                title: prog.name,
                                message: next ? 'Pendaftaran DIBUKA' : 'Pendaftaran DITUTUP',
                              });
                            }}
                            className={`w-full py-2 px-3 rounded-xl text-[11px] font-label font-bold uppercase tracking-wider transition-all flex items-center justify-center gap-1.5 shadow-xs ${
                              prog.isRegistrationOpen !== false
                                ? 'bg-primary text-on-primary hover:bg-primary/90'
                                : 'bg-surface-container-high text-on-surface-variant hover:bg-surface-container-highest'
                            }`}
                          >
                            <Globe size={13} />
                            <span>
                              {prog.isRegistrationOpen !== false
                                ? 'Buka (Aktif)'
                                : 'Tutup (Nonaktif)'}
                            </span>
                          </button>
                        </div>

                        {/* Mode Persetujuan */}
                        <div className="p-3.5 rounded-2xl bg-surface-container-low/50 border border-on-surface/5 space-y-2">
                          <span className="text-[10px] font-label font-bold uppercase tracking-widest text-on-surface-variant opacity-60 block">
                            Mode Persetujuan
                          </span>
                          <div className="flex gap-1.5">
                            <button
                              type="button"
                              onClick={() => {
                                updateProgramMutation.mutate({
                                  id: prog.id,
                                  data: { approvalMode: 'MANUAL' },
                                });
                                addNotification({
                                  type: 'success',
                                  title: prog.name,
                                  message: 'Mode diubah ke Seleksi Manual',
                                });
                              }}
                              className={`flex-1 py-2 px-2 rounded-xl text-[10px] font-label font-bold uppercase tracking-wider transition-all ${
                                prog.approvalMode !== 'AUTO_APPROVE'
                                  ? 'bg-primary text-on-primary shadow-xs'
                                  : 'bg-surface-container text-on-surface-variant hover:bg-surface-container-high'
                              }`}
                            >
                              Manual
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                updateProgramMutation.mutate({
                                  id: prog.id,
                                  data: { approvalMode: 'AUTO_APPROVE' },
                                });
                                addNotification({
                                  type: 'success',
                                  title: prog.name,
                                  message: 'Mode diubah ke Otomatis (Auto-Approve)',
                                });
                              }}
                              className={`flex-1 py-2 px-2 rounded-xl text-[10px] font-label font-bold uppercase tracking-wider transition-all ${
                                prog.approvalMode === 'AUTO_APPROVE'
                                  ? 'bg-primary text-on-primary shadow-xs'
                                  : 'bg-surface-container text-on-surface-variant hover:bg-surface-container-high'
                              }`}
                            >
                              Auto
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Public Registration Link */}
                      <div className="p-3.5 rounded-2xl bg-primary/5 border border-primary/10 space-y-2">
                        <span className="text-[10px] font-label font-bold uppercase tracking-widest text-primary font-mono block">
                          Tautan Form Pendaftaran Publik
                        </span>
                        <div className="p-2 rounded-xl bg-surface-container-lowest border border-primary/15 font-mono text-[11px] text-on-surface break-all">
                          {publicUrl}
                        </div>
                      </div>
                    </div>

                    {/* Card Footer Actions */}
                    <div className="flex gap-2 pt-2 border-t border-on-surface/5">
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(publicUrl);
                            setCopiedProgramCode(prog.code);
                            addNotification({
                              type: 'success',
                              title: 'Tautan Disalin',
                              message: `Link form pendaftaran untuk program ${prog.code} berhasil disalin.`,
                            });
                            setTimeout(() => setCopiedProgramCode(null), 2500);
                          } catch {
                            // ignore
                          }
                        }}
                        className="flex-1 py-2.5 px-4 rounded-xl bg-primary text-on-primary text-xs font-label font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 shadow-xs hover:bg-primary/90 transition-all"
                      >
                        {isCopied ? <Check size={14} /> : <Copy size={14} />}
                        <span>{isCopied ? 'Tersalin!' : 'Salin Tautan Form'}</span>
                      </button>

                      <a
                        href={publicUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="px-4 py-2.5 rounded-xl bg-surface-container-high hover:bg-surface-container text-on-surface text-xs font-label font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all no-underline"
                        title="Buka Formulir Pendaftaran"
                      >
                        <ExternalLink size={14} />
                        <span>Buka</span>
                      </a>

                      <Link
                        to={`/programs/${prog.id}`}
                        className="px-4 py-2.5 rounded-xl bg-surface-container text-on-surface-variant hover:text-primary hover:bg-surface-container-high text-xs font-label font-bold uppercase tracking-wider flex items-center justify-center transition-all no-underline"
                        title="Lihat Detail & Kurikulum Program"
                      >
                        <ChevronRight size={16} />
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* TAB 3: WHATSAPP & TELEGRAM GROUP LINKS */}
      {activeMainTab === 'WHATSAPP' && (
        <div className="space-y-8 animate-fadeIn">
          {/* Header Bar */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-surface-container-lowest p-6 sm:p-8 rounded-3xl shadow-level1 border border-on-surface/5">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-primary font-bold text-[10px] uppercase tracking-widest font-label">
                <MessageCircle size={14} className="text-secondary" />
                <span>Saluran Komunitas Pendaftaran</span>
              </div>
              <h2 className="text-2xl font-headline font-black text-on-surface m-0 tracking-tight">
                Tautan Grup Undangan WhatsApp & Telegram
              </h2>
              <p className="text-sm font-body text-on-surface-variant max-w-2xl opacity-70">
                Tautan grup otomatis diberikan kepada peserta pendaftaran yang telah disetujui.
                Kuota klik dihitung hingga 1000 anggota per grup.
              </p>
            </div>

            <Button
              onClick={() => {
                setEditingWhatsAppGroup(undefined);
                setIsWhatsAppModalOpen(true);
              }}
              className="h-12 px-6 rounded-xl flex items-center gap-2 text-xs font-label font-bold uppercase tracking-widest shadow-level2"
            >
              <Plus size={16} />
              <Bilingual {...copy.registrations.btnCreateGroup} />
            </Button>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-4">
            <div className="space-y-1">
              <span className="text-[10px] font-label font-bold uppercase tracking-widest text-on-surface-variant opacity-60 pl-1">
                Filter Program
              </span>
              <select
                className="bg-surface-container-high border-none rounded-xl px-4 py-2.5 text-xs font-body text-on-surface outline-none min-w-[200px]"
                value={selectedWhatsAppProgramId}
                onChange={e => setSelectedWhatsAppProgramId(e.target.value)}
              >
                <option value="ALL">Semua Program</option>
                {programsList.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.code} - {p.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <span className="text-[10px] font-label font-bold uppercase tracking-widest text-on-surface-variant opacity-60 pl-1">
                Kategori Gender
              </span>
              <select
                className="bg-surface-container-high border-none rounded-xl px-4 py-2.5 text-xs font-body text-on-surface outline-none min-w-[150px]"
                value={selectedWhatsAppGender}
                onChange={e => setSelectedWhatsAppGender(e.target.value)}
              >
                <option value="ALL">Semua Kategori</option>
                <option value="IKHWAN">Ikhwan</option>
                <option value="AKHWAT">Akhwat</option>
                <option value="MIXED">Campuran (Mixed)</option>
              </select>
            </div>
          </div>

          {/* Groups Grid */}
          {isGroupsLoading ? (
            <div className="flex flex-col items-center justify-center min-h-[300px] space-y-4">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
              <p className="text-sm font-body text-on-surface-variant">Memuat saluran grup...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredGroups.map(group => {
                const programName =
                  programsList.find(p => p.id === group.programId)?.name || 'Program Belajar';
                const isCopied = copiedGroupLink === group.id;

                return (
                  <div
                    key={group.id}
                    className="bg-surface-container-lowest p-6 rounded-3xl shadow-level1 border border-on-surface/5 flex flex-col justify-between space-y-6 hover:shadow-level2 transition-all"
                  >
                    <div className="space-y-4">
                      <div className="flex justify-between items-start">
                        <div className="p-3 bg-primary/10 text-primary rounded-2xl">
                          <MessageSquare size={20} />
                        </div>
                        <span
                          className={`text-[9px] font-label font-bold px-2.5 py-1 rounded-full uppercase tracking-wider ${
                            group.gender === 'IKHWAN'
                              ? 'bg-blue-100 text-blue-800'
                              : group.gender === 'AKHWAT'
                                ? 'bg-pink-100 text-pink-800'
                                : 'bg-surface-container-high text-on-surface-variant'
                          }`}
                        >
                          {group.gender}
                        </span>
                      </div>

                      <div>
                        <span className="text-[10px] font-mono text-primary font-bold block mb-1">
                          {programName}
                        </span>
                        <h3 className="text-lg font-headline font-bold text-on-surface m-0">
                          {group.name}
                        </h3>
                        {group.description && (
                          <p className="text-xs font-body text-on-surface-variant mt-1 line-clamp-2 opacity-70">
                            {group.description}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2 pt-2 border-t border-on-surface/5">
                        <div className="flex items-center justify-between text-xs font-body text-on-surface-variant">
                          <span className="flex items-center gap-1.5 opacity-60">
                            <Users size={13} />
                            Kapasitas Maksimal:
                          </span>
                          <span className="font-mono font-bold text-on-surface">
                            {group.maxMembers || 1000} anggota
                          </span>
                        </div>

                        {group.inviteLink ? (
                          <div className="p-2.5 rounded-xl bg-surface-container-low border border-on-surface/5 font-mono text-[11px] text-on-surface break-all truncate">
                            {group.inviteLink}
                          </div>
                        ) : (
                          <div className="p-2.5 rounded-xl bg-amber-50 text-amber-800 text-[11px] font-body">
                            Tautan undangan belum dikonfigurasi
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-2 border-t border-on-surface/5">
                      {group.inviteLink && (
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(group.inviteLink || '');
                              setCopiedGroupLink(group.id);
                              addNotification({
                                type: 'success',
                                title: 'Tautan Disalin',
                                message: `Tautan grup ${group.name} berhasil disalin.`,
                              });
                              setTimeout(() => setCopiedGroupLink(null), 2500);
                            } catch {
                              // ignore
                            }
                          }}
                          className="flex-1 py-2 px-3 rounded-xl bg-primary/10 hover:bg-primary text-primary hover:text-on-primary text-xs font-label font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all"
                        >
                          {isCopied ? <Check size={13} /> : <Copy size={13} />}
                          <span>{isCopied ? 'Tersalin' : 'Salin Link'}</span>
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => {
                          setEditingWhatsAppGroup(group);
                          setIsWhatsAppModalOpen(true);
                        }}
                        className="py-2 px-3 rounded-xl bg-surface-container-high hover:bg-surface-container text-on-surface-variant text-xs font-label font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all"
                      >
                        <Pencil size={13} />
                        <span>Edit</span>
                      </button>

                      <Link
                        to={`/whatsapp-groups/${group.id}`}
                        className="py-2 px-3 rounded-xl bg-surface-container text-on-surface-variant hover:text-primary hover:bg-surface-container-high text-xs font-label font-bold uppercase tracking-wider flex items-center justify-center transition-all no-underline"
                      >
                        <ExternalLink size={13} />
                      </Link>
                    </div>
                  </div>
                );
              })}

              {filteredGroups.length === 0 && (
                <div className="col-span-full py-20 text-center bg-surface-container-low/20 rounded-3xl border-2 border-dashed border-outline-variant/10 italic text-on-surface-variant opacity-50 font-body">
                  Belum ada saluran grup untuk filter yang dipilih.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      <RejectionModal
        isOpen={!!rejectionModalId}
        onClose={() => setRejectionModalId(null)}
        onConfirm={reason => rejectionModalId && handleReject(rejectionModalId, reason)}
        isPending={rejectMutation.isPending}
        participantName={registrations.find(r => r.id === rejectionModalId)?.user?.name || ''}
      />

      {editingProgram && (
        <ProgramModal program={editingProgram} onClose={() => setEditingProgram(null)} />
      )}

      {isWhatsAppModalOpen && (
        <WhatsAppGroupModal
          group={editingWhatsAppGroup}
          initialProgramId={
            selectedWhatsAppProgramId !== 'ALL' ? selectedWhatsAppProgramId : undefined
          }
          onClose={() => {
            setIsWhatsAppModalOpen(false);
            setEditingWhatsAppGroup(undefined);
          }}
        />
      )}

      {editUserContext && (
        <EditUserModal
          userId={editUserContext.userId}
          missingFields={editUserContext.missingFields}
          reason={editUserContext.reason}
          primaryActionLabel={copy.users.btnSaveAndApprove}
          onClose={() => setEditUserContext(null)}
          onSaved={() => {
            // Retry the approval now that the profile is complete.
            const registrationId = editUserContext.registrationId;
            setEditUserContext(null);
            approveMutation.mutate(
              { id: registrationId, adminNotes: 'Approved via Admin Panel' },
              {
                onError: err => {
                  // If the backend still rejects (e.g. another field is
                  // missing on a future schema), re-open the modal with
                  // the new missingFields list.
                  if (isIncompleteUserProfileError(err)) {
                    setEditUserContext({
                      userId: err.body.userId,
                      registrationId: err.body.registrationId,
                      missingFields: err.body.missingFields,
                      reason: err.body?.message || err.message,
                    });
                  }
                },
              }
            );
          }}
        />
      )}
    </div>
  );
}
